import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  differenceInDays,
  getDate,
  getDaysInMonth,
  startOfMonth,
} from "date-fns";
import { useMemo } from "react";
import { useCompanyMutation } from "@/hooks/useCompanyMutation";
import { supabase } from "@/integrations/supabase/client";

export function useGrowthData(activeCompanyId: string | null) {
  const queryClient = useQueryClient();

  // Buscar Serviços para Restrição
  const servicesQuery = useQuery({
    queryKey: ["services", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("services")
        .select("name")
        .eq("company_id", activeCompanyId);
      if (error) throw error;
      return data;
    },
    enabled: !!activeCompanyId,
  });

  // Buscar Cupons
  const couponsQuery = useQuery({
    queryKey: ["coupons", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("tenant_coupons")
        .select("*")
        .eq("company_id", activeCompanyId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!activeCompanyId,
  });

  const saveCoupon = useCompanyMutation({
    mutationFn: async (variables: any) => {
      if (variables.id) {
        const { error } = await supabase
          .from("tenant_coupons")
          .update({
            code: variables.code,
            description: variables.description,
            type: variables.type,
            value: variables.value,
            restricted_service: variables.restricted_service || null,
          })
          .eq("id", variables.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("tenant_coupons").insert({
          company_id: activeCompanyId,
          code: variables.code,
          description: variables.description,
          type: variables.type,
          value: variables.value,
          restricted_service: variables.restricted_service || null,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupons"] });
    },
  });

  const toggleCoupon = useCompanyMutation({
    mutationFn: async (c: any) => {
      const { error } = await supabase
        .from("tenant_coupons")
        .update({ status: c.status === "ATIVO" ? "INATIVO" : "ATIVO" })
        .eq("id", c.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["coupons"] }),
  });

  // Buscar Ordens do Mês Atual (para Previsão de Faturamento)
  const monthOrdersQuery = useQuery({
    queryKey: ["growth_month_orders", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const start = startOfMonth(new Date()).toISOString();
      const { data, error } = await supabase
        .from("orders")
        .select("total, created_at")
        .eq("company_id", activeCompanyId)
        .gte("created_at", start)
        .in("status", ["CONCLUIDO", "PAGO"]);
      if (error) throw error;
      return data;
    },
    enabled: !!activeCompanyId,
  });

  // Buscar Clientes e Últimas Ordens (Para Churn)
  const churnQuery = useQuery({
    queryKey: ["growth_churn", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      // Trazemos os clientes e tentamos achar a última vez que vieram
      const { data: customers, error: cErr } = await supabase
        .from("customers")
        .select("id, name, phone, created_at")
        .eq("company_id", activeCompanyId);
      if (cErr) throw cErr;

      const { data: allOrders, error: oErr } = await supabase
        .from("orders")
        .select("customer_id, created_at")
        .eq("company_id", activeCompanyId);
      if (oErr) throw oErr;

      // Mapear última visita
      const lastVisits: Record<string, Date> = {};
      allOrders.forEach((o) => {
        if (!o.customer_id) return;
        const d = new Date(o.created_at);
        if (!lastVisits[o.customer_id] || d > lastVisits[o.customer_id]) {
          lastVisits[o.customer_id] = d;
        }
      });

      const now = new Date();
      return customers
        .map((c) => {
          const lastVisit = lastVisits[c.id] || new Date(c.created_at);
          const daysAway = differenceInDays(now, lastVisit);
          return {
            ...c,
            lastVisit,
            daysAway,
          };
        })
        .filter((c) => c.daysAway >= 45) // Define Churn como 45+ dias
        .sort((a, b) => b.daysAway - a.daysAway);
    },
    enabled: !!activeCompanyId,
  });

  // Cálculo da Previsão de Faturamento
  const revenueMetrics = useMemo(() => {
    const today = new Date();
    const currentDay = getDate(today);
    const totalDays = getDaysInMonth(today);

    const monthOrders = monthOrdersQuery.data || [];
    const currentTotal = monthOrders.reduce(
      (sum, o) => sum + Number(o.total),
      0,
    );

    // Evitar divisão por zero se for dia 1
    const avgPerDay =
      currentDay > 1 ? currentTotal / (currentDay - 1) : currentTotal;
    const predictedTotal = avgPerDay * totalDays;

    return { currentTotal, predictedTotal, avgPerDay };
  }, [monthOrdersQuery.data]);

  return {
    services: servicesQuery.data || [],
    coupons: couponsQuery.data || [],
    saveCoupon,
    toggleCoupon,
    churnData: churnQuery.data || [],
    loadingChurn: churnQuery.isLoading,
    revenueMetrics,
    loadingOrders: monthOrdersQuery.isLoading,
  };
}
